/* eslint-disable no-nested-ternary */
import { Edit } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Promotion } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';

import PromotionModal from './PromotionModal';

type Props = {
  promo: DbDocument<Promotion>;
};

const PromoStatus = ({ promo }: Props) => {
  const { lang } = useLocale();
  const now = Date.now();

  if (now < promo.startTime) {
    return (
      <Typography
        variant="caption"
        sx={{ p: 1, borderRadius: 1, bgcolor: (t) => `${t.palette.info.main}22`, color: (t) => t.palette.info.dark }}
      >
        {t.scheduled[lang]}
      </Typography>
    );
  }
  if (now > promo.endTime) {
    return (
      <Typography
        variant="caption"
        sx={{ p: 1, borderRadius: 1, bgcolor: (t) => `${t.palette.grey[900]}22`, color: (t) => t.palette.grey[900] }}
      >
        {t.completed[lang]}
      </Typography>
    );
  }
  return (
    <Typography
      variant="caption"
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: (t) => `${t.palette.success.main}22`,
        color: (t) => t.palette.success.dark,
      }}
    >
      {t.active[lang]}
    </Typography>
  );
};

type Row = DbDocument<Promotion>;

const Promotions = () => {
  const { lang } = useLocale();
  const { promotions, worker } = useCafe();
  const { cafe } = useAdmin();
  const [open, setOpen] = useState(false);
  const [promotion, setPromotion] = useState<DbDocument<Promotion> | null>(null);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => r.name,
      },
      {
        name: 'discount',
        title: t.discount[lang],
        align: 'left',
        render: (r: Row) =>
          r.discountType === 'fixed_discount' ? currency(r.discountValue, cafe?.currency, true) : `${r.discountValue}%`,
      },
      {
        name: 'status',
        title: t.status[lang],
        align: 'left',
        render: (r: Row) => <PromoStatus promo={r} />,
      },
      {
        name: 'edit',
        title: '',
        align: 'right',
        render: (r: Row) => (
          <IconButton
            onClick={() => {
              setPromotion(r);
              setOpen(true);
            }}
            disabled={!worker?.credentials.create_and_edit_promotions}
          >
            <Edit />
          </IconButton>
        ),
      },
    ],
    [cafe?.currency, lang, worker?.credentials.create_and_edit_promotions]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTable
        actionButton={
          <PrimaryButton
            onClick={() => setOpen(true)}
            variant="contained"
            disabled={!worker?.credentials.create_and_edit_promotions}
          >
            {t.add_promotion[lang]}
          </PrimaryButton>
        }
        columns={columns}
        rows={promotions || []}
        onRowClick={(p) => setPromotion(p)}
        dataTestIdPrefix="promotion"
      />

      {open && (
        <PromotionModal
          promotion={promotion}
          onClose={() => {
            setOpen(false);
            setPromotion(null);
          }}
        />
      )}
    </Box>
  );
};

export default Promotions;
